// src/hooks/useTransactions.js
// Merges sales + expenses + money transfers into a single normalized list
// into a single normalized list for the Transactions page.
 
import { useState, useEffect } from "react"
import { getDb } from "../lib/db"
import { getTransfers } from "../repositories/stockTransfersRepository"
import { useResolvedBusinessId } from "./useRole"

function formatAccountLabel(account) {
  if (!account) return ""
  if (account === "mpesa") return "M-Pesa"
  if (account === "bank" || account === "bank_transfer") return "Bank"
  return account.charAt(0).toUpperCase() + account.slice(1)
}
 
export function useTransactions(branchId = null) {
  const { businessId } = useResolvedBusinessId()
 
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
 
  useEffect(() => {
    if (!businessId) {
      setLoading(false)
      setError(null)
      return
    }
 
    let active = true
    let subscriptions = []
 
    const init = async () => {
      if (!active) return
 
      setLoading(true)
      setError(null)
 
      const timeoutId = setTimeout(() => {
        if (active) {
          setError(new Error("Timeout: Unable to load transactions"))
          setLoading(false)
        }
      }, 10000)
 
      try {
        const db = await getDb()
        if (!active) return
        clearTimeout(timeoutId)

        const toPlain = (doc) => (doc?.toJSON ? doc.toJSON() : doc)

        const groupBySaleId = (docs) => docs.reduce((acc, doc) => {
          const row = toPlain(doc)
          if (!row?.sale_id) return acc
          if (!acc[row.sale_id]) acc[row.sale_id] = []
          acc[row.sale_id].push(row)
          return acc
        }, {})
 
        const collectionConfigs = [
          {
            collection: db.sales,
            type: "sale",
            dateField: "date",
            selector: {
              business_id: businessId,
              _deleted: { $ne: true },
              ...(branchId ? { branch_id: branchId } : {}),
            },
          },
          {
            collection: db.expenses,
            type: "expense",
            dateField: "created_at",
            selector: {
              business_id: businessId,
              _deleted: { $ne: true },
              ...(branchId ? { branch_id: branchId } : {}),
            },
          },
        ].filter(({ collection }) => Boolean(collection))
 
        const refresh = async () => {
          if (!active) return
 
          const [results, transferRows] = await Promise.all([
            Promise.all(
              collectionConfigs.map(async (config) => {
                const docs = await config.collection
                  .find({
                    selector: config.selector,
                    sort: [{ [config.dateField]: "desc" }, { id: "desc" }],
                  })
                  .exec()
                return docs.map((doc) => {
                  const row = toPlain(doc)
                  if (config.type === "sale") {
                    return { ...row, type: "sale", date: row.date || row.created_at || null }
                  }
                  if (config.type === "expense") {
                    return { ...row, type: "expense", date: row.date || row.created_at || null }
                  }
                  return row
                })
              })
            ),
            getTransfers({ businessId, branchId: branchId || undefined }).catch(() => []),
          ])

          const saleRows = results.flat().filter((row) => row.type === "sale")
          const saleIds = saleRows.map((row) => row.id).filter(Boolean)
          const [saleItemDocs, salePaymentDocs] = await Promise.all([
            saleIds.length > 0
              ? (db.sale_items
                ? db.sale_items.find({ selector: { sale_id: { $in: saleIds }, _deleted: { $ne: true } } }).exec().catch(() => [])
                : Promise.resolve([]))
              : Promise.resolve([]),
            saleIds.length > 0
              ? (db.sale_payments
                ? db.sale_payments.find({ selector: { sale_id: { $in: saleIds }, _deleted: { $ne: true } } }).exec().catch(() => [])
                : Promise.resolve([]))
              : Promise.resolve([]),
          ])

          const saleItemsBySaleId = groupBySaleId(saleItemDocs || [])
          const salePaymentsBySaleId = groupBySaleId(salePaymentDocs || [])
 
          if (!active) return
 
          const transferRowsWithType = (transferRows || []).map((row) => ({
            ...row,
            type: "transfer",
            date: row.date || row.created_at || null,
          }))

          const normalized = results
            .flat()
            .map((row) => {
              if (row.type !== "sale") return row
              return {
                ...row,
                sale_items: saleItemsBySaleId[row.id] || row.sale_items || [],
                sale_payments: salePaymentsBySaleId[row.id] || row.sale_payments || [],
              }
            })
            .concat(transferRowsWithType)
            .map((row) => normalizeTransaction(row))
            .filter(Boolean)
            .sort((a, b) => {
              const ta = new Date(a.date || 0).getTime()
              const tb = new Date(b.date || 0).getTime()
              if (ta !== tb) return tb - ta
              return String(b.id || "").localeCompare(String(a.id || ""))
            })
 
          setTransactions(normalized)
          setLoading(false)
        }
 
        await refresh()
 
        subscriptions = collectionConfigs.map((config) =>
          config.collection
            .find({ selector: config.selector })
            .$
            .subscribe({
              next: () => {
                refresh().catch((err) => {
                  if (!active) return
                  setError(err)
                  setLoading(false)
                })
              },
              error: (err) => {
                if (!active) return
                setError(err)
                setLoading(false)
              },
            })
        )
      } catch (err) {
        if (!active) return
        clearTimeout(timeoutId)
        setError(err)
        setLoading(false)
      }
    }
 
    init()
 
    return () => {
      active = false
      subscriptions.forEach((s) => s?.unsubscribe?.())
    }
  }, [businessId, branchId])
 
  return { transactions, loading, error }
}
 
function normalizeTransaction(transaction) {
  if (!transaction) {
    return null
  }
 
  const sourceType = transaction.source_type || transaction.type || null

  const isSale =
    sourceType === "sale" ||
    Array.isArray(transaction.sale_items) ||
    typeof transaction.total_amount === "number" ||
    typeof transaction.subtotal === "number"

  if (isSale) {
    const saleItems = Array.isArray(transaction.sale_items) ? transaction.sale_items : []
    const amount = transaction.total_amount ??
      transaction.amount ??
      saleItems.reduce((sum, item) => sum + Number(item?.total_amount || 0), 0)
 
    const displayName =
      transaction.display_name ||
      (saleItems.length > 1
        ? `${saleItems[0]?.product_name || "Sale"} +${saleItems.length - 1} more`
        : saleItems[0]?.product_name || "Sale")
 
    const payments = Array.isArray(transaction.sale_payments) ? transaction.sale_payments : []
    const primaryPayment = payments.length === 0
      ? transaction.payment_account || "cash"
      : payments.reduce((max, p) => Number(p?.amount || 0) > Number(max?.amount || 0) ? p : max, payments[0]).payment_method
 
    return {
      ...transaction,
      type: "sale",
      source_type: "sale",
      source_id: transaction.source_id || transaction.id,
      amount: Number(amount || 0),
      display_name: displayName,
      payment_account: primaryPayment,
      lifecycle_state: transaction.lifecycle_state || "finalized",
      sale: {
        id: transaction.id,
        total_amount: Number(amount || 0),
        lifecycle_state: transaction.lifecycle_state || "finalized",
        etims_receipt_no: transaction.etims_receipt_no ?? null,
        sale_items: saleItems,
        sale_payments: payments,
      },
      sale_items: saleItems,
      sale_payments: payments,
    }
  }
 
  const isExpense =
    sourceType === "expense" ||
    Array.isArray(transaction.expenses) ||
    typeof transaction.category === "string"

  if (isExpense) {
    return {
      ...transaction,
      type: "expense",
      source_type: "expense",
      source_id: transaction.source_id || transaction.id,
      amount: Number(transaction.amount || 0),
      display_name: transaction.description || transaction.category || "Expense",
      payment_account: transaction.payment_account || "cash",
      date: transaction.created_at || transaction.date || new Date().toISOString(),
      lifecycle_state: "finalized",
      expense: {
        category: transaction.category,
        description: transaction.description,
        date: transaction.date || transaction.created_at || new Date().toISOString(),
      },
    }
  }

  if (sourceType === "stock_entry" || transaction.type === "stock_entry") {
    return {
      ...transaction,
      type: "stock_entry",
      source_type: "stock_entry",
      source_id: transaction.source_id || transaction.id,
      amount: Number(transaction.amount || 0),
      display_name:
        transaction.display_name ||
        `Stock receipt: ${transaction.product_name || transaction.product_id || "Stock entry"}`,
      payment_account: transaction.payment_account || "cash",
      lifecycle_state: transaction.lifecycle_state || "finalized",
    }
  }
 
  if (sourceType === "transfer") {
    return {
      ...transaction,
      type: "transfer",
      source_type: "transfer",
      source_id: transaction.source_id || transaction.id,
      amount: Number(transaction.amount || 0),
      display_name:
        transaction.display_name ||
        `Transfer: ${formatAccountLabel(transaction.from_account)} → ${formatAccountLabel(transaction.to_account)}`,
      payment_account: transaction.payment_account || transaction.from_account || null,
      lifecycle_state: transaction.lifecycle_state || "finalized",
      transfer: {
        id: transaction.id,
        from_account: transaction.from_account || null,
        to_account: transaction.to_account || null,
        amount: Number(transaction.amount || 0),
        transaction_cost: Number(transaction.transaction_cost || 0),
        note: transaction.note || null,
      },
    }
  }
 
  return null
}